import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAppStore } from '../stores/appStore'
import { useFeatureFlag } from '../lib/useFeatureFlag'

/**
 * BugReporter — floating "Something's broken?" button + report sheet.
 *
 * Gated by the W4 feature flag `bug_reporter` (off-by-default). When enabled
 * for the caller's family, renders a small "?" bubble at bottom-right; tap
 * opens a sheet with a one-line summary textarea + Submit. The client
 * auto-attaches current URL, viewport, and user_agent. family_id comes from
 * the app store. Server derives reporter_id from the JWT (never trusted
 * from the client).
 *
 * Zero-visual-surface when the flag is off — safe to mount globally without
 * shifting any sensor baselines. Wren v999999 chunk W1 (2026-09-11).
 */
type SubmitState = 'idle' | 'sending' | 'sent' | 'error'

export function BugReporter(): JSX.Element | null {
  const familyId = useAppStore((s) => s.familyId)
  const enabled = useFeatureFlag(familyId, 'bug_reporter')
  const [open, setOpen] = useState(false)
  const [summary, setSummary] = useState('')
  const [status, setStatus] = useState<SubmitState>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (open) textareaRef.current?.focus()
  }, [open])

  const reset = useCallback(() => {
    setSummary('')
    setStatus('idle')
    setErrorMsg(null)
  }, [])

  const close = useCallback(() => {
    setOpen(false)
    reset()
  }, [reset])

  const submit = useCallback(async () => {
    if (!familyId || !summary.trim() || status === 'sending') return
    setStatus('sending')
    setErrorMsg(null)
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      const url = `${import.meta.env.VITE_SUPABASE_URL ?? ''}/functions/v1/bug-report-received`
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          family_id: familyId,
          summary: summary.trim(),
          url: window.location.href,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          user_agent: navigator.userAgent,
        }),
      })
      const payload = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null
      if (!res.ok || !payload?.ok) {
        setErrorMsg(payload?.error ?? `http_${res.status}`)
        setStatus('error')
        return
      }
      setStatus('sent')
      setTimeout(close, 1200)
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e))
      setStatus('error')
    }
  }, [familyId, summary, status, close])

  if (!enabled || !familyId) return null

  return (
    <>
      {!open && (
        <button
          type="button"
          aria-label="Report a bug"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-4 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-bg-elevated text-text-secondary shadow-md ring-1 ring-black/5 hover:bg-bg-base active:scale-[0.98]"
        >
          <span className="text-lg font-semibold">?</span>
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bug-reporter-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) close()
          }}
        >
          <div className="w-full max-w-md rounded-t-2xl bg-bg-base p-4 shadow-xl sm:rounded-2xl">
            <div className="mb-2 flex items-center justify-between">
              <h2 id="bug-reporter-title" className="text-body font-semibold text-text-primary">
                Something's broken?
              </h2>
              <button
                type="button"
                aria-label="Close"
                onClick={close}
                className="rounded-full px-2 py-1 text-text-tertiary hover:text-text-primary"
              >
                ✕
              </button>
            </div>
            <textarea
              ref={textareaRef}
              value={summary}
              onChange={(e) => setSummary(e.target.value.slice(0, 500))}
              placeholder="One line — what went wrong?"
              rows={3}
              maxLength={500}
              className="w-full resize-none rounded-lg border border-bg-elevated bg-bg-base p-3 text-body text-text-primary placeholder:text-text-tertiary focus:border-pack-early-care focus:outline-none"
              disabled={status === 'sending' || status === 'sent'}
            />
            <div className="mt-1 text-caption text-text-tertiary">
              We'll auto-attach the current URL, viewport, and browser. No PII beyond your family ID.
            </div>
            {status === 'error' && errorMsg && (
              <div className="mt-2 text-caption text-red-600">Failed: {errorMsg}. Try again?</div>
            )}
            {status === 'sent' && (
              <div className="mt-2 text-caption text-green-600">Thanks — Dan will see this shortly.</div>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={close}
                className="rounded-lg px-3 py-2 text-body text-text-secondary hover:bg-bg-elevated"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!summary.trim() || status === 'sending' || status === 'sent'}
                className="rounded-lg bg-pack-early-care px-4 py-2 text-body font-medium text-white disabled:opacity-50"
              >
                {status === 'sending' ? 'Sending…' : status === 'sent' ? 'Sent' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
